import { useCallback, useMemo, useState } from 'react';
import { MANAGE_TIN_COLLECTION_FAIL } from '@bo-constants/member/memberCopy';
import type { MemberTinStatusErrorResponse } from '@library/types/members';
import { useUpdateMemberTinStatusMutation } from '@bo-services/members/membersApi';

export interface MemberTinStatusProps {
  memberId: string;
  needsTid?: boolean;
  tidValidation: number;
}

export const useMemberTinStatus = ({
  memberId,
  needsTid,
  tidValidation
}: MemberTinStatusProps) => {
  const [tidValidationValue, setTidValidationValue] = useState(tidValidation);
  const [promptSSN, setPromptSSN] = useState(!!needsTid);

  const [
    updateTinStatus,
    {
      error: submitError,
      isLoading: isSubmitLoading,
      isError: isSubmitError,
      isSuccess: isSubmitSuccess
    }
  ] = useUpdateMemberTinStatusMutation();

  const showConfirmationMessage = isSubmitError || isSubmitSuccess;

  const errorMessage = useMemo(() => {
    const message = (submitError as MemberTinStatusErrorResponse)?.error[0];
    return message ? `Error: ${message}` : MANAGE_TIN_COLLECTION_FAIL;
  }, [submitError]);

  const currentTidStatus = useMemo(() => {
    switch (tidValidation) {
      case 1:
        return 'Current status: Validated (1)';
      case -2:
        return 'Current status: 1st attempt rejected (-2)';
      case -4:
        return 'Current status: 2nd attempt rejected (-4)';
      default:
        return 'Current status: Unvalidated (0)';
    }
  }, [tidValidation]);

  const handleTidValidationChange = useCallback((value: string) => {
    setTidValidationValue(Number(value));
  }, []);

  const handlePromptSSNChange = useCallback(() => {
    setPromptSSN((prev) => !prev);
  }, []);

  const handleSubmit = useCallback(async () => {
    await updateTinStatus({
      member_id: memberId,
      needs_tid: promptSSN,
      tid_validation: tidValidationValue
    }).unwrap();
  }, [memberId, promptSSN, tidValidationValue, updateTinStatus]);

  return {
    currentTidStatus,
    errorMessage,
    handlePromptSSNChange,
    handleTidValidationChange,
    handleSubmit,
    isSubmitError,
    isSubmitLoading,
    isSubmitSuccess,
    promptSSN,
    showConfirmationMessage,
    tidValidationValue
  };
};
